import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API_URL } from "../../../config";
import { useNavigate } from "react-router-dom";

const formatMinutes = (seconds = 0) => {
  const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const mins = Math.floor(totalSeconds / 60);
  const hrs = Math.floor(mins / 60);
  const rmins = mins % 60;
  const rsecs = totalSeconds % 60;
  return hrs > 0 ? `${hrs}h ${rmins}m ${rsecs}s` : `${rmins}m ${rsecs}s`;
};

export default function EmployeeTask() {
  const navigate = useNavigate();
  const intervalsRef = useRef(null);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [statusForm, setStatusForm] = useState({
    status: "",
    reason: "",
    progress: "",
    file: null,
  });

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState("all");

  const employeeLocal = JSON.parse(localStorage.getItem("user"));
  const employeeId = employeeLocal?.employeeId || employeeLocal?._id || localStorage.getItem("chatUserId");

  const fetchTasks = async () => {
    if (!employeeId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/tasks/employee/${employeeId}`);
      const rawTasks = res.data.tasks || [];

      const runningTask = rawTasks.find((t) => {
        const last = t.timeLogs?.length > 0 ? t.timeLogs[t.timeLogs.length - 1] : null;
        return last && !last.endAt;
      });

      const prepared = rawTasks.map((t) => {
        const last = t.timeLogs?.length > 0 ? t.timeLogs[t.timeLogs.length - 1] : null;
        return {
          ...t,
          running: runningTask && runningTask._id === t._id && last && !last.endAt,
          liveSeconds: 0,
        };
      });

      setTasks(prepared);
    } catch (err) {
      console.error("Task fetch failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const submitCompletedDirectly = async (task) => {
    try {
      await axios.patch(`${API_URL}/api/tasks/TaskStatus/${task._id}`, { status: "Completed" });
      fetchTasks();
    } catch (err) {
      alert("Failed to mark task as completed");
    }
  };

  const getStatusInfo = (task) => {
    const lastStatus = task.statusHistory?.length > 0 ? task.statusHistory[task.statusHistory.length - 1] : null;
    const lastComment = task.comments?.length > 0 ? task.comments[task.comments.length - 1] : null;

    if (task.status === "Pending" && lastStatus?.reason) return `Reason: ${lastStatus.reason}`;
    if (task.status === "In Progress") {
      let text = "";
      if (lastStatus?.progress) text += `Progress: ${lastStatus.progress}%`;
      if (lastStatus?.reason) text += text ? ` | ${lastStatus.reason}` : lastStatus.reason;
      return text || "Work in progress";
    }
    if (task.status === "Completed") return "Completed";
    if (lastComment?.text) return `Comment: ${lastComment.text}`;
    return "-";
  };

  useEffect(() => {
    fetchTasks();
  }, [employeeId]);

  useEffect(() => {
    intervalsRef.current = setInterval(() => {
      setTasks((prev) =>
        prev.map((t) => {
          if (!t.running) return t;
          const last = t.timeLogs[t.timeLogs.length - 1];
          const liveSeconds = Math.floor((Date.now() - new Date(last.startAt)) / 1000);
          return { ...t, liveSeconds };
        })
      );
    }, 1000);
    return () => clearInterval(intervalsRef.current);
  }, []);

  useEffect(() => {
    const autoStop = () => {
      if (!employeeId) return;
      navigator.sendBeacon(`${API_URL}/api/tasks/autoStopTimer/${employeeId}`);
    };
    window.addEventListener("beforeunload", autoStop);
    window.addEventListener("unload", autoStop);
    return () => {
      window.removeEventListener("beforeunload", autoStop);
      window.removeEventListener("unload", autoStop);
    };
  }, [employeeId]);

  const startTimer = async (task) => {
    try {
      await axios.post(`${API_URL}/api/tasks/timerStart/${task._id}`);
      fetchTasks();
    } catch (err) {
      alert(err?.response?.data?.message || "Cannot start timer");
    }
  };

  const stopTimer = async (task) => {
    try {
      await axios.post(`${API_URL}/api/tasks/stopTimer/${task._id}`);
      fetchTasks();
    } catch (err) {
      alert(err?.response?.data?.message || "Cannot stop timer");
    }
  };

  const openStatusModal = (task, status) => {
    setCurrentTask(task);
    setStatusForm({ status, reason: "", progress: "", file: null });
    setShowModal(true);
  };

  const submitStatus = async () => {
    if (!currentTask?._id) {
      alert("Task not selected");
      return;
    }
    const form = new FormData();
    form.append("status", statusForm.status);
    form.append("reason", statusForm.reason);
    if (statusForm.progress) form.append("progress", statusForm.progress);
    if (statusForm.file) form.append("attachment", statusForm.file);

    await axios.patch(`${API_URL}/api/tasks/TaskStatus/${currentTask._id}`, form);
    setShowModal(false);
    fetchTasks();
  };

  const openTask = (id) => navigate(`/employee/TaskView/${id}`);

  const filteredTasks = tasks.filter((t) => {
    const date = new Date(t.startDate || t.updatedAt);
    if (isNaN(date)) return false;
    if (Number(year) !== date.getFullYear()) return false;
    if (month !== "all" && Number(month) !== date.getMonth() + 1) return false;
    return true;
  });

  if (loading) return <div className="p-4 text-center">Loading tasks...</div>;

  return (
    <div className="emp-page-shell">
      <h2 className="emp-page-title">Task Dashboard</h2>
      <p className="emp-page-subtitle">Manage your active tasks, time logs, and status updates.</p>

      <section className="emp-panel">
        <div className="emp-panel-body">
          <div className="emp-toolbar">
            <div className="d-flex gap-2">
              <select className="form-select" value={year} onChange={(e) => setYear(e.target.value)}>
                {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>

              <select className="form-select" value={month} onChange={(e) => setMonth(e.target.value)}>
                <option value="all">All Months</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i} value={i + 1}>
                    {new Date(0, i).toLocaleString("default", { month: "long" })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="emp-table-wrap emp-task-table-wrap">
            <table className="table table-hover align-middle emp-task-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Task</th>
                  <th>Project</th>
                  <th>Priority</th>
                  <th>Due Date</th>
                  <th>Time Spent</th>
                  <th>Status</th>
                  <th>Status Action</th>
                  <th>Actions</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.length === 0 && (
                  <tr>
                    <td colSpan="10" className="text-center text-muted py-4">
                      No tasks found
                    </td>
                  </tr>
                )}

                {filteredTasks.map((t, index) => (
                  <tr key={t._id}>
                    <td>{index + 1}</td>
                    <td className="fw-semibold">{t.title}</td>
                    <td>{t.projectId?.projectName || "-"}</td>
                    <td>{t.priority}</td>
                    <td>{t.dueDate ? new Date(t.dueDate).toLocaleDateString() : "-"}</td>
                    <td>{formatMinutes((t.timeSpent || 0) + (t.running ? t.liveSeconds : 0))}</td>
                    <td>{t.status}</td>
                    <td>
                      <div className="dropdown">
                        <button className="btn btn-outline-secondary btn-sm dropdown-toggle emp-action-btn" data-bs-toggle="dropdown">
                          Update Status
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end">
                          {["Pending", "In Progress", "Completed"].map((s) => (
                            <li key={s}>
                              <button
                                className="dropdown-item"
                                disabled={t.status === "Completed" && s === "Completed"}
                                onClick={() => {
                                  if (s === "Completed") submitCompletedDirectly(t);
                                  else openStatusModal(t, s);
                                }}
                              >
                                {s}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        {!t.running ? (
                          <button
                            className="btn btn-outline-primary btn-sm emp-action-btn emp-start-btn"
                            disabled={tasks.some((x) => x.running)}
                            onClick={() => startTimer(t)}
                          >
                            Start
                          </button>
                        ) : (
                          <button className="btn btn-outline-danger btn-sm emp-action-btn emp-stop-btn" onClick={() => stopTimer(t)}>
                            Stop
                          </button>
                        )}
                        <button className="btn btn-info btn-sm text-white emp-action-btn emp-view-btn" onClick={() => openTask(t._id)}>
                          View
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="fw-semibold">{getStatusInfo(t)}</div>
                      <small className="text-muted">{t.updatedAt ? new Date(t.updatedAt).toLocaleString() : ""}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {showModal && (
        <div className="modal show d-block" style={{ background: "#0006" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Update Status to {statusForm.status}</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>

              <div className="modal-body">
                <textarea
                  className="form-control mb-2"
                  placeholder="Reason"
                  value={statusForm.reason}
                  onChange={(e) => setStatusForm({ ...statusForm, reason: e.target.value })}
                />

                {statusForm.status === "In Progress" && (
                  <input
                    type="number"
                    className="form-control mb-2"
                    placeholder="% Completed"
                    min="1"
                    max="100"
                    value={statusForm.progress}
                    onChange={(e) => setStatusForm({ ...statusForm, progress: e.target.value })}
                  />
                )}

                <input
                  type="file"
                  className="form-control"
                  accept=".jpg,.png,.pdf"
                  onChange={(e) => setStatusForm({ ...statusForm, file: e.target.files[0] })}
                />
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={submitStatus}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
